import {type ElementType, FC, useMemo} from "react";
import type {TextFieldProps} from "@mui/material/TextField";
import {useForkRef} from "@mui/material/utils";
import type {GridFilterInputValueProps} from "@mui/x-data-grid";
import {useGridRootProps} from "@mui/x-data-grid-pro";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import type {Dayjs} from "dayjs";
import dayjs from "dayjs";
import {getAppDateInputFormat} from "@/app2/shared/utils/dateManipulation";

const DeliveryDateOnlyFilterTextField: FC<{
	params: TextFieldProps;
	focusElementRef: GridFilterInputValueProps["focusElementRef"];
	BaseTextField: ElementType<TextFieldProps>;
	baseTextFieldProps: Partial<TextFieldProps>;
}> = ({params, focusElementRef, BaseTextField, baseTextFieldProps}) => {
	const inputRef = useForkRef(
		focusElementRef as React.Ref<HTMLInputElement>,
		params.inputRef as React.Ref<HTMLInputElement>
	);

	return (
		<BaseTextField
			{...params}
			{...baseTextFieldProps}
			className="announcements-delivery-filter-field"
			variant="standard"
			inputRef={inputRef}
			InputLabelProps={{
				...params.InputLabelProps,
				...baseTextFieldProps.InputLabelProps,
				shrink: true
			}}
			InputProps={{
				...params.InputProps,
				...baseTextFieldProps.InputProps
			}}
			inputProps={{
				...params.inputProps,
				...baseTextFieldProps.inputProps,
				placeholder: getAppDateInputFormat()
			}}
		/>
	);
};

/**
 * Date-only filter using the same MUI + TextField pattern as `DeliveryDateTimeFilterInput`.
 * Shows the current app locale format while storing `YYYY-MM-DD` for the picked local calendar day.
 */
export const DeliveryDateOnlyFilterInput: FC<GridFilterInputValueProps> = ({
	item,
	applyValue,
	apiRef,
	focusElementRef
}) => {
	const rootProps = useGridRootProps();
	const BaseTextField = rootProps.components.BaseTextField;
	const baseTextFieldProps = rootProps.componentsProps?.baseTextField ?? {};

	const value = useMemo((): Dayjs | null => {
		if (item.value == null || item.value === "") {
			return null;
		}
		const d = dayjs(String(item.value));
		return d.isValid() ? d : null;
	}, [item.value]);

	const handleChange = (newValue: Dayjs | null) => {
		applyValue({
			...item,
			value: newValue && newValue.isValid() ? newValue.format("YYYY-MM-DD") : undefined
		});
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<DatePicker
				inputFormat={getAppDateInputFormat()}
				label={apiRef.current.getLocaleText("filterPanelInputLabel")}
				value={value}
				onChange={handleChange}
				OpenPickerButtonProps={{
					"aria-label": "Choose date",
					sx: {color: "inherit"}
				}}
				renderInput={(params: TextFieldProps) => (
					<DeliveryDateOnlyFilterTextField
						params={params}
						focusElementRef={focusElementRef}
						BaseTextField={BaseTextField}
						baseTextFieldProps={baseTextFieldProps}
					/>
				)}
			/>
		</LocalizationProvider>
	);
};
